import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from 'react-router-dom';
import QuantityControl from '../helper/QuantityControl';
import ThankYouPopup from "./ThankYouPopup";
import ApplyCouponModal from "./orders/ApplyCouponModal";
import SelectBagTypyModel from "./orders/SelectBagTypyModel";
import OrderDetailsModal from "./orders/OrderDetailsModal";
import PrescriptionUpload from "./orders/PrescriptionUpload";
import RemoveCartItemModal from "./orders/RemoveCartItemModal";
import ProductImage from "./common/ProductImage";
import { getSegmentId, getSegmentItems, requiresSegmentAddress } from "../utils/segmentFlow";
import { getProductDetailLink } from "../utils/productDetailNavigation";
import { getPromoCodeList, applyRemovePromoCode, removeCartItem, updateCartItemQuantity, getPlaceOrder } from "../redux/actions/action-creator";
import {
    DEFAULT_DELIVERY_LOCATION,
    LOCATION_STORAGE_KEY,
    getMarketMetadata,
    isGuestDefaultLocation,
} from "../utils/locationDefaults";
import "./cartsection.css";

const TIP_STEP = 1;
const DAY_NAME_FORMATTER = new Intl.DateTimeFormat("en-US", { weekday: "long" });

const addDays = (date, days) => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);

    return nextDate;
};

const parseDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

    const stringValue = String(value);
    const dateParts = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const parsedDate = dateParts
        ? new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]))
        : new Date(stringValue);

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getDeliveryDateLabel = (date) => `${date.getDate()} ${DAY_NAME_FORMATTER.format(date)}`;

const DEFAULT_LOCATION = DEFAULT_DELIVERY_LOCATION;

const getSlotLabel = (slot) => {
    if (typeof slot === "string") return slot;

    const directLabel =
        slot?.label ||
        slot?.name ||
        slot?.title ||
        slot?.time ||
        slot?.slot_time ||
        slot?.slot ||
        slot?.time_slot ||
        slot?.time_slot_text ||
        slot?.slot_time_text ||
        slot?.display_time ||
        slot?.delivery_time;

    if (directLabel) return directLabel;

    return (
        [
            slot?.start_time ||
            slot?.startTime ||
            slot?.from_time ||
            slot?.from ||
            slot?.start ||
            slot?.slot_start_time,
            slot?.end_time ||
            slot?.endTime ||
            slot?.to_time ||
            slot?.to ||
            slot?.end ||
            slot?.slot_end_time,
        ]
            .filter(Boolean)
            .join("-")
    );
};

const getSavedUserLocation = () => {
    try {
        const savedValue = localStorage.getItem(LOCATION_STORAGE_KEY);
        return savedValue ? JSON.parse(savedValue) : null;
    } catch (error) {
        return null;
    }
};

const getActiveUserLocation = (authState = {}) => {
    const reduxLocation = authState.user_location || {};
    const savedLocation = getSavedUserLocation() || {};

    return {
        latitude:
            authState.latitude ??
            reduxLocation.latitude ??
            savedLocation.latitude ??
            DEFAULT_LOCATION.latitude,
        longitude:
            authState.longitude ??
            reduxLocation.longitude ??
            savedLocation.longitude ??
            DEFAULT_LOCATION.longitude,
        merchant_id:
            reduxLocation.merchant_id ??
            reduxLocation.merchantId ??
            savedLocation.merchant_id ??
            savedLocation.merchantId ??
            DEFAULT_LOCATION.merchant_id,
        area:
            authState.area ??
            reduxLocation.area ??
            reduxLocation.area_id ??
            savedLocation.area ??
            savedLocation.area_id ??
            null,
        country_id:
            authState.country_id ??
            reduxLocation.country_id ??
            reduxLocation.countryId ??
            savedLocation.country_id ??
            savedLocation.countryId ??
            DEFAULT_LOCATION.country_id,
    };
};

const getLocationAddressId = (locationData = {}) => {
    const addressId =
        locationData.selected_address_id ||
        locationData.user_address_id ||
        locationData.address_id ||
        locationData.id ||
        "";

    return isGuestDefaultLocation(locationData) ? "" : addressId;
};

const getSelectedUserAddressId = (authState = {}) => {
    const reduxAddressId = getLocationAddressId(authState.user_location || {});

    if (reduxAddressId) {
        return reduxAddressId;
    }

    return getLocationAddressId(getSavedUserLocation() || {});
};

const getCartItemId = (item) =>
    String(
        item?.product_variant_id ||
        item?.variant_id ||
        item?.product_id ||
        item?.variant?.id ||
        item?.product?.id ||
        item?.id ||
        item?.cart_item_id ||
        ""
    );

const getCartItemName = (item) =>
    item?.name ||
    item?.title ||
    item?.product_name ||
    item?.product_title ||
    item?.product?.name ||
    item?.product?.title ||
    item?.variant?.name ||
    item?.variant?.title ||
    "Product";

const getCartItemImage = (item) =>
    item?.image ||
    item?.product_cover_image ||
    item?.product_image ||
    item?.thumbnail ||
    item?.variant?.image ||
    item?.product?.image ||
    item?.product?.product_cover_image ||
    "";

const getCartItemUnit = (item) =>
    item?.weight_unit ||
    item?.unit ||
    item?.portion ||
    item?.product_unit ||
    item?.quantity_label ||
    item?.variant?.weight_unit ||
    item?.variant?.unit ||
    item?.product?.weight_unit ||
    item?.product?.unit ||
    "";

const toNumber = (value, fallback = 0) => {
    const normalizedValue = Number(String(value ?? "").replace(/,/g, "").replace(/[^0-9.-]/g, ""));

    return Number.isFinite(normalizedValue) ? normalizedValue : fallback;
};

const getCartItemPrice = (item) =>
    toNumber(
        item?.price ??
        item?.product_price ??
        item?.selling_price ??
        item?.product_selling_price ??
        item?.product_cost ??
        item?.final_price ??
        item?.discounted_price ??
        item?.unit_price ??
        item?.variant?.price ??
        item?.variant?.selling_price ??
        item?.product?.price ??
        0
    );

const getCartItemCurrency = (item, fallbackCurrency) =>
    item?.currency || item?.currency_code || item?.product?.currency || fallbackCurrency;

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const isPlainObject = (value) =>
    value && typeof value === "object" && !Array.isArray(value);

const parseJsonValue = (value) => {
    if (typeof value !== "string") return value;

    try {
        return JSON.parse(value);
    } catch (error) {
        return value;
    }
};

const getValue = (item, keys) => {
    const matchedKey = keys.find((key) => hasValue(item?.[key]));

    return matchedKey ? item[matchedKey] : "";
};

const getFirstValue = (sources, keys) => {
    for (const source of sources) {
        const value = getValue(source, keys);
        if (hasValue(value)) return value;
    }

    return "";
};

const getNumberValue = (sources, keys, fallback = 0) => {
    const value = getFirstValue(sources, keys);

    if (!hasValue(value)) return fallback;

    const normalizedValue = Number(String(value).replace(/,/g, "").replace(/[^0-9.-]/g, ""));

    return Number.isFinite(normalizedValue) ? normalizedValue : fallback;
};

const getNestedNumberValue = (source, keys, fallback = 0) => {
    if (!source || typeof source !== "object") return fallback;

    const visited = new WeakSet();
    const findValue = (obj) => {
        if (!obj || typeof obj !== "object" || visited.has(obj)) return undefined;
        visited.add(obj);

        const directValue = getFirstValue([obj], keys);
        if (hasValue(directValue)) return directValue;

        for (const nested of Object.values(obj)) {
            if (typeof nested === "object") {
                const found = findValue(nested);
                if (hasValue(found)) return found;
            }
        }

        return undefined;
    };

    const value = findValue(source);
    if (!hasValue(value)) return fallback;

    const normalizedValue = Number(String(value).replace(/,/g, "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(normalizedValue) ? normalizedValue : fallback;
};

const getBooleanValue = (sources, keys, fallback = false) => {
    const value = getFirstValue(sources, keys);

    if (!hasValue(value)) return fallback;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;

    const normalizedValue = String(value).trim().toLowerCase();
    return ["1", "true", "yes", "y", "on"].includes(normalizedValue);
};

const getArrayValue = (sources, keys) => {
    for (const source of sources) {
        const value = parseJsonValue(getValue(source, keys));

        if (Array.isArray(value)) return value;
        if (isPlainObject(value)) {
            const nestedArray = Object.values(value).find(Array.isArray);
            if (nestedArray) return nestedArray;
        }
    }

    return [];
};

const getProductDetailsFromCartData = (cartData) => {
    const productDetails = getArrayValue([cartData], [
        "product_details",
        "product_detail",
        "products",
        "items",
        "cart_items",
        "cart_products",
        "cart_product_details",
        "response_data",
        "order_details",
        "details",
    ]);

    return productDetails;
};

const formatAddress = (addressData) => {
    if (typeof addressData === "string") return addressData;
    if (Array.isArray(addressData)) return formatAddress(addressData[0]);
    if (!addressData || typeof addressData !== "object") return "";

    const directAddress = getValue(addressData, [
        "formatted_address",
        "full_address",
        "complete_address",
        "delivery_address",
        "address",
    ]);

    if (typeof directAddress === "string" && directAddress.trim()) {
        return directAddress;
    }

    return [
        "house_no",
        "flat_no",
        "building_name",
        "address_line_1",
        "address_line_2",
        "street",
        "landmark",
        "area",
        "area_name",
        "city",
        "state",
        "country",
        "pincode",
    ]
        .map((key) => addressData[key])
        .filter(Boolean)
        .join(", ");
};

const normalizePaymentMethods = (paymentData) => {
    const parsedPaymentData = parseJsonValue(paymentData);
    const source =
        Array.isArray(parsedPaymentData)
            ? parsedPaymentData
            : Array.isArray(parsedPaymentData?.payment_methods)
                ? parsedPaymentData.payment_methods
                : Array.isArray(parsedPaymentData?.methods)
                    ? parsedPaymentData.methods
                    : Array.isArray(parsedPaymentData?.options)
                        ? parsedPaymentData.options
                        : [];

    return source
        .map((item, index) => {
            if (typeof item === "string") {
                return { id: item, label: item };
            }

            const label = getValue(item, [
                "label",
                "name",
                "title",
                "payment_method",
                "payment_mode",
                "display_name",
            ]);

            if (!label) return null;

            return {
                id: getValue(item, ["id", "code", "slug"]) || `${label}-${index}`,
                label,
                icon: getValue(item, ["icon", "image", "logo"]),
            };
        })
        .filter(Boolean);
};

const getDeliverySlotSource = (item) => {
    if (Array.isArray(item)) return item;
    if (!isPlainObject(item)) return [item];

    const directSlots =
        item.service_time_slot ||
        item.slots ||
        item.time_slots ||
        item.slot_details ||
        item.time_slot_details ||
        item.delivery_slots ||
        item.available_slots ||
        item.available_time_slots ||
        item.timings ||
        item.times ||
        item.slot ||
        item.time_slot ||
        item.time;
    const parsedDirectSlots = parseJsonValue(directSlots);

    if (Array.isArray(parsedDirectSlots)) return parsedDirectSlots;
    if (hasValue(parsedDirectSlots)) return [parsedDirectSlots];

    const nestedArray = Object.values(item)
        .map(parseJsonValue)
        .find(Array.isArray);

    return nestedArray || [item];
};

const normalizeDeliverySlotDates = (timeSlotDetails, inheritedDetails = {}) => {
    const parsedTimeSlotDetails = parseJsonValue(timeSlotDetails);

    if (!Array.isArray(parsedTimeSlotDetails)) {
        const nestedSlots =
            parsedTimeSlotDetails?.dates ||
            parsedTimeSlotDetails?.days ||
            parsedTimeSlotDetails?.time_slot_details ||
            parsedTimeSlotDetails?.time_slots ||
            parsedTimeSlotDetails?.slots ||
            (parsedTimeSlotDetails && typeof parsedTimeSlotDetails === "object"
                ? Object.values(parsedTimeSlotDetails).find(Array.isArray)
                : null);

        return Array.isArray(nestedSlots)
            ? normalizeDeliverySlotDates(nestedSlots, { ...inheritedDetails, ...parsedTimeSlotDetails })
            : [];
    }

    const today = new Date();

    return parsedTimeSlotDetails
        .map((item, index) => {
            const explicitDate = isPlainObject(item)
                ? parseDateValue(
                    getValue(item, [
                        "date",
                        "slot_date",
                        "delivery_date",
                        "delivery_day",
                        "day_date",
                    ])
                )
                : null;
            const weekDay = hasValue(item?.day) ? Number(item.day) : NaN;
            const date = explicitDate || addDays(today,
                Number.isInteger(weekDay) && weekDay >= 0 && weekDay <= 6
                    ? (weekDay - today.getDay() + 7) % 7
                    : index
            );
            const label = getDeliveryDateLabel(date);
            const slots = getDeliverySlotSource(item);
            const normalizedSlots = slots.map(parseJsonValue).map((slot, slotIndex) => {
                const slotLabel = getSlotLabel(slot);
                if (!slotLabel) return null;
                const serviceTimeSlotId = getFirstValue([slot, item, inheritedDetails], ["service_time_slot_id"]) ||
                    (slot !== item ? getValue(item, ["id"]) : "") || null;
                const serviceTimeSlotDetailId = getFirstValue([slot, item, inheritedDetails], ["service_time_slot_detail_id"]) ||
                    getValue(slot, ["id"]) || null;

                return {
                    value: hasValue(serviceTimeSlotDetailId)
                        ? `${serviceTimeSlotId}:${serviceTimeSlotDetailId}`
                        : `${slotLabel}-${slotIndex}`,
                    label: slotLabel,
                    service_time_slot_id: serviceTimeSlotId,
                    service_time_slot_detail_id: serviceTimeSlotDetailId,
                };
            }).filter(Boolean);

            if (normalizedSlots.length === 0) return null;

            return {
                id: String(
                    item?.date_id ||
                    item?.id ||
                    `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}-${index}`
                ),
                label,
                date,
                slots: normalizedSlots,
            };
        })
        .filter(Boolean)
        .sort((left, right) => left.date - right.date);
};

const normalizeSelectableOptions = (optionData, fallbackOptions = []) => {
    const parsedOptionData = parseJsonValue(optionData);
    const nestedData = parseJsonValue(parsedOptionData?.data);
    const source =
        Array.isArray(parsedOptionData)
            ? parsedOptionData
            : Array.isArray(parsedOptionData?.options)
                ? parsedOptionData.options
                : Array.isArray(nestedData)
                    ? nestedData
                    : Array.isArray(nestedData?.options)
                        ? nestedData.options
                        : Array.isArray(nestedData?.items)
                            ? nestedData.items
                            : Array.isArray(nestedData?.response_data)
                                ? nestedData.response_data
                                : Array.isArray(nestedData?.cell_contents)
                                    ? nestedData.cell_contents
                                    : Array.isArray(nestedData?.coupon_list)
                                        ? nestedData.coupon_list
                                        : Array.isArray(nestedData?.coupons)
                                            ? nestedData.coupons
                                            : Array.isArray(nestedData?.promo_codes)
                                                ? nestedData.promo_codes
                                                : Array.isArray(nestedData?.promo_code_list)
                                                    ? nestedData.promo_code_list
                    : Array.isArray(parsedOptionData?.items)
                        ? parsedOptionData.items
                        : Array.isArray(parsedOptionData?.response_data)
                            ? parsedOptionData.response_data
                            : Array.isArray(parsedOptionData?.cell_contents)
                                ? parsedOptionData.cell_contents
                                : Array.isArray(parsedOptionData?.coupon_list)
                                    ? parsedOptionData.coupon_list
                                    : Array.isArray(parsedOptionData?.coupons)
                                        ? parsedOptionData.coupons
                                        : Array.isArray(parsedOptionData?.promo_codes)
                                            ? parsedOptionData.promo_codes
                                            : Array.isArray(parsedOptionData?.promo_code_list)
                                                ? parsedOptionData.promo_code_list
                                                : Array.isArray(parsedOptionData?.vouchers)
                                                    ? parsedOptionData.vouchers
                                                    : Array.isArray(parsedOptionData?.offers)
                                                        ? parsedOptionData.offers
                                                        : fallbackOptions;

    return source
        .map((item, index) => {
            if (typeof item === "string") {
                return { id: item, label: item, code: item };
            }

            const label = getValue(item, [
                "label",
                "name",
                "title",
                "bag_name",
                "coupon_name",
                "display_name",
                "code",
                "promocode",
                "promoCode",
                "coupon_code",
                "couponCode",
                "promo_code",
                "promo_code_value",
                "voucher_code",
                "voucherCode",
            ]);

            if (!label) return null;

            const price = getValue(item, ["price", "amount", "charges", "charge"]);
            const code = getValue(item, [
                "promocode",
                "promoCode",
                "promo_code",
                "promo_code_value",
                "coupon_code",
                "couponCode",
                "code",
                "voucher_code",
                "voucherCode",
                "value",
            ]) || label;

            return {
                id: getValue(item, ["id", "code", "slug"]) || `${label}-${index}`,
                label: hasValue(price) ? `${label} (${price})` : label,
                title: getValue(item, ["title", "name", "coupon_name", "promo_code_name", "voucher_name"]) || label,
                description: getValue(item, [
                    "description",
                    "detail",
                    "message",
                    "coupon_description",
                    "promo_code_description",
                    "terms",
                    "terms_conditions",
                ]),
                code,
            };
        })
        .filter(Boolean);
};

const normalizeShoppingBagOptions = (shoppingBags = [], currency = "AED") => {
    const parsedShoppingBags = parseJsonValue(shoppingBags);
    const source =
        Array.isArray(parsedShoppingBags)
            ? parsedShoppingBags
            : Array.isArray(parsedShoppingBags?.shopping_bags)
                ? parsedShoppingBags.shopping_bags
                : Array.isArray(parsedShoppingBags?.bags)
                    ? parsedShoppingBags.bags
                    : Array.isArray(parsedShoppingBags?.data)
                        ? parsedShoppingBags.data
                        : [];

    return source
        .map((item, index) => {
            if (typeof item === "string") {
                return { id: item, label: item, value: item, shoppingBagId: null };
            }

            const name = getValue(item, [
                "label",
                "name",
                "title",
                "bag_name",
                "bag_title",
                "shopping_bag_name",
                "shopping_bag_title",
                "shopping_bag",
                "bag_type",
                "type",
            ]);

            if (!name) return null;

            const price = getValue(item, [
                "price",
                "amount",
                "charges",
                "charge",
                "bag_price",
                "bag_charges",
                "shopping_bag_price",
                "shopping_bag_charges",
            ]);
            const formattedPrice = hasValue(price)
                ? String(price).includes(currency)
                    ? price
                    : `${currency} ${toNumber(price, 0).toFixed(2)}`
                : "";
            const label = formattedPrice ? `${name} (${formattedPrice})` : name;

            return {
                ...item,
                id: getValue(item, ["id", "bag_id", "shopping_bag_id", "code", "slug"]) || `${name}-${index}`,
                shoppingBagId: getValue(item, ["shopping_bag_id", "bag_id", "id"]) || null,
                label,
                value: label,
            };
        })
        .filter(Boolean);
};

const getProgressPercent = (amount, target) => {
    const numericTarget = Number(target || 0);

    if (!numericTarget) return 0;

    return Math.min(100, Math.max(0, (Number(amount || 0) / numericTarget) * 100));
};

const appendIfValue = (formData, key, value) => {
    if (hasValue(value)) {
        formData.append(key, value);
    }
};

const normalizeCashChangeOptions = (config) => {
    const parsedConfig = parseJsonValue(config);

    if (!parsedConfig) return [];

    const source = Array.isArray(parsedConfig)
        ? parsedConfig
        : Array.isArray(parsedConfig?.options)
            ? parsedConfig.options
            : Array.isArray(parsedConfig?.data)
                ? parsedConfig.data
                : isPlainObject(parsedConfig)
                    ? Object.entries(parsedConfig).map(([key, value]) => ({
                        key,
                        value,
                    }))
                    : [];

    return source
        .map((item, index) => {
            if (typeof item === "string" || typeof item === "number") {
                return {
                    id: String(item),
                    label: String(item),
                    value: item,
                };
            }

            const optionKey = item.key || getValue(item, ["id", "code", "value", "slug"]) || index;
            const rawValue = item.value;
            const optionValue = isPlainObject(rawValue)
                ? getValue(rawValue, ["value", "code", "id"]) || optionKey
                : hasValue(rawValue)
                    ? rawValue
                    : optionKey;
            const optionLabel = isPlainObject(rawValue)
                ? getValue(rawValue, ["label", "name", "title", "text", "description"]) || optionValue
                : getValue(item, ["label", "name", "title", "text", "description"]) ||
                    (String(optionKey).toLowerCase() === "none" ? "None" : String(rawValue || optionKey));

            return {
                id: String(optionKey),
                label: optionLabel,
                value: optionValue,
            };
        })
        .filter((item) => hasValue(item.value));
};

const isCashPaymentOption = (option = {}) => {
    const paymentText = `${option.id || ""} ${option.code || ""} ${option.label || ""}`.toLowerCase();

    return paymentText.includes("cash") || paymentText.includes("cod");
};

const CartSection = ({ cartState: cartStateProp, onOrderPlaced }) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const reduxCartState = useSelector((state) => state.cart || {});
    const authState = useSelector((state) => state.auth || {});
    const servicesData = useSelector((state) => state.common?.servicesData);
    const cartState = cartStateProp || reduxCartState;
    const cartData = cartState.data || {};
    const parsedReceipt = parseJsonValue(cartData.receipt);
    const {
        payment_method,
        time_slot_details
    } = cartData;
    const receiptData = isPlainObject(parsedReceipt) ? parsedReceipt : {};
    const cartSources = [receiptData, cartData];
    const cartTimeSlotDetails =
        getFirstValue(cartSources, [
            "time_slot_details",
            "time_slots",
            "slots",
            "delivery_slots",
            "delivery_slot_details",
            "available_slots",
            "available_time_slots",
        ]) ||
        time_slot_details;
    const apiProductDetails = getProductDetailsFromCartData(cartData);
    const localCartItems = Array.isArray(cartState.cartItems) ? cartState.cartItems : [];
    const product_details = localCartItems.length > 0
        ? localCartItems
        : apiProductDetails.filter(Boolean);
    const isCartLoading = Boolean(cartState.saveCartLoading || cartState.getCartLoading);
    const [showPopup, setShowPopup] = useState(false);
    const [showBagTypeModal, setShowBagTypeModal] = useState(false);
    const [showCouponModal, setShowCouponModal] = useState(false);
    const [showOrderDetails, setShowOrderDetails] = useState(false);
    const [orderError, setOrderError] = useState("");
    const [submittingOrder, setSubmittingOrder] = useState(false);
    const orderRequestRef = useRef(false);
    const orderErrorRef = useRef(null);
    const [selectedBagType, setSelectedBagType] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState("");
    const [appliedCouponAmount, setAppliedCouponAmount] = useState(null);
    const [couponMessage, setCouponMessage] = useState({ type: "", text: "" });
    const [allowSubstitution, setAllowSubstitution] = useState(false);
    const [deliveryMode, setDeliveryMode] = useState("");
    const [selectedDeliveryDate, setSelectedDeliveryDate] = useState("");
    const [selectedDeliverySlot, setSelectedDeliverySlot] = useState("");
    const [selectedPaymentOption, setSelectedPaymentOption] = useState("");
    const [selectedCashChangeOption, setSelectedCashChangeOption] = useState("");
    const [specialNotes, setSpecialNotes] = useState("");
    const [prescriptionImage, setPrescriptionImage] = useState(null);
    const [tipAmount, setTipAmount] = useState(0);
    const [isTipEdited, setIsTipEdited] = useState(false);
    const [promoCodeData, setPromoCodeData] = useState([]);
    const [promoCodeLoading, setPromoCodeLoading] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState(null);
    const [removingProduct, setRemovingProduct] = useState(false);
    const [removeError, setRemoveError] = useState("");
    const removeRequestRef = useRef(false);
    const promoRequestRef = useRef(false);

    const userLocation = getActiveUserLocation(authState);
    const activeMarket = getMarketMetadata(userLocation);

    const removeProduct = (id) => {
        if (isCartLoading || pendingRemoval || removeRequestRef.current) return;
        const product = product_details.find((item) => getCartItemId(item) === String(id));
        if (!product) return;
        setRemoveError("");
        setPendingRemoval({ id, cartId, name: getCartItemName(product) });
    };

    const closeRemoveConfirmation = useCallback(() => {
        if (removeRequestRef.current) return;
        setPendingRemoval(null);
        setRemoveError("");
    }, []);

    const confirmRemoveProduct = async () => {
        if (!pendingRemoval || isCartLoading || removeRequestRef.current) return;
        if (pendingRemoval.cartId !== cartId || !product_details.some((item) => getCartItemId(item) === String(pendingRemoval.id))) {
            closeRemoveConfirmation();
            return;
        }
        removeRequestRef.current = true;
        setRemovingProduct(true);
        setRemoveError("");
        try {
            const response = await dispatch(removeCartItem(pendingRemoval.id, { cartId }));
            if ([1, "1", true].includes(response?.result)) setPendingRemoval(null);
            else setRemoveError("Unable to remove this item. Please try again.");
        } catch (error) {
            setRemoveError(error?.message || "Unable to remove this item. Please try again.");
        } finally {
            removeRequestRef.current = false;
            setRemovingProduct(false);
        }
    };

    const updateQuantity = (id, newQty) => {
        dispatch(updateCartItemQuantity(id, newQty, { cartId }));
    };

    const handleApplyCoupon = async (couponCode) => {
        if (promoRequestRef.current || isCartLoading) return { success: false, message: "Please wait for the current cart update." };
        if (!couponCode || !cartId) {
            setCouponMessage({ type: "error", text: "Cart not ready yet. Please try again." });
            return;
        }

        promoRequestRef.current = true;
        setPromoCodeLoading(true);
        setCouponMessage({ type: "", text: "" });

        const payload = {
            cart_id: cartId,
            segment_id: promoSegmentId,
            latitude: String(userLocation.latitude),
            longitude: String(userLocation.longitude),
            promo_code: couponCode,
            tip_amount: String(displayTipAmount || 0),
            merchant_id: userLocation.merchant_id,
            area: userLocation.area,
            order_amount: String(itemTotal || 0),
            country_area_id: userLocation.area,
        };

        try {
            const response = await dispatch(applyRemovePromoCode(payload));
            if (![1, "1"].includes(response?.result)) throw response || {};
            const message =
                (response && (response.message || response.msg || response.success_message)) ||
                `Coupon ${couponCode} applied successfully`;
            const responseCouponCode =
                getFirstValue([response], ["promo_code", "coupon_code", "code", "couponCode", "promoCode"]) ||
                couponCode;
            const responseDiscount = getNestedNumberValue(response, ["discount_amount", "coupon_amount", "coupon_discount", "discount"], null);

            setAppliedCoupon(responseCouponCode);
            setAppliedCouponAmount(responseDiscount);
            setCouponMessage({ type: "success", text: message });

            return {
                success: true,
                message,
                couponCode: responseCouponCode,
                discount: responseDiscount,
            };
        } catch (error) {
            const response = error || {};
            const message =
                (response && (response.message || response.msg || response.error || response.error_message)) ||
                "Unable to apply this coupon. Please try again.";
            setCouponMessage({ type: "error", text: String(message) });

            return {
                success: false,
                message: String(message),
            };
        } finally {
            promoRequestRef.current = false;
            setPromoCodeLoading(false);
        }
    };

    const getCheckoutError = () => {
        if (!cartId || product_details.length === 0) return "Your cart is empty. Please add a product before placing an order.";
        if (isShoppingBagRequired && !selectedBagOption) return "Please select a shopping bag.";
        if (isShoppingBagRequired && selectedBagOption && !hasValue(selectedBagOption.shoppingBagId)) return "This shopping bag is unavailable. Please select another bag.";
        if (!["instant", "scheduled"].includes(deliveryMode)) return "Please select instant delivery or a delivery date and time slot.";
        if (deliveryMode === "scheduled" && !selectedDeliverySlotData) {
            return "Please select a delivery date and time slot.";
        }
        if (deliveryMode === "scheduled" && (!hasValue(selectedDeliverySlotData.service_time_slot_id) || !hasValue(selectedDeliverySlotData.service_time_slot_detail_id))) {
            return "This delivery slot is unavailable. Please choose another slot or instant delivery.";
        }
        if (!paymentOptions.some((option) => String(option.id) === String(selectedPaymentOption))) return "Please select a payment method.";
        return "";
    };

    const handlePlaceOrder = () => {
        if (isCartLoading || orderRequestRef.current) return;
        const error = getCheckoutError();
        setOrderError(error);
        if (error) {
            requestAnimationFrame(() => orderErrorRef.current?.focus());
            return;
        }
        if (requiresOrderDetails) setShowOrderDetails(true);
        else submitOrder();
    };

    const submitOrder = async (details) => {
        if (orderRequestRef.current || isCartLoading) return;
        const validationError = getCheckoutError();
        if (validationError) { setOrderError(validationError); return; }
        orderRequestRef.current = true;
        setSubmittingOrder(true);
        setOrderError("");
        const payload = {
            cart_id: cartId,
            payment_method_id: selectedPaymentOption,
            segment_id: promoSegmentId,
            address: deliveryAddress,
            delivery_type: deliveryMode === "instant" ? "1" : "2",
            additional_notes: specialNotes,
            substitution: allowSubstitution ? "1" : "0",
            user_address_id: getSelectedUserAddressId(authState),
            latitude: userLocation.latitude,
            longitude: userLocation.longitude,
            shopping_bag_id: isShoppingBagRequired ? selectedBagOption?.shoppingBagId ?? null : null,
            service_time_slot_id: deliveryMode === "scheduled" ? selectedDeliverySlotData.service_time_slot_id : null,
            service_time_slot_detail_id: deliveryMode === "scheduled" ? selectedDeliverySlotData.service_time_slot_detail_id : null,
            service_type_id: null,
            // These are the confirmed FLOWER_DELIVERY backend fields. Other new request keys are deferred.
            ...(details ? {
                sender_name: details.anonymous ? "" : details.senderName,
                receiver_name: details.receiverName,
                receiver_phone: `${details.phoneCode}${details.receiverPhone}`,
                message: details.message.trim(),
                home_or_office: details.homeOrOffice,
            } : {}),
        };

        try {
            let requestPayload = payload;
            if (showPrescriptionUpload && prescriptionImage) {
                requestPayload = new FormData();
                Object.entries(payload).forEach(([key, value]) => requestPayload.append(key, value ?? ""));
                requestPayload.append("prescription_image", prescriptionImage, prescriptionImage.name);
            }
            const response = await dispatch(getPlaceOrder(requestPayload));
            if (response?.result === true || response?.result === 1 || response?.result === "1") {
                setShowOrderDetails(false);
                if (onOrderPlaced) onOrderPlaced(response);
                else setShowPopup(true);
            } else setOrderError(response?.message || "Unable to place your order. Please try again.");
        } catch (error) {
            const message = error?.response?.data?.message || error?.message;
            setOrderError(typeof message === "string" ? message : "Unable to place your order. Please try again.");
        } finally {
            orderRequestRef.current = false;
            setSubmittingOrder(false);
        }
    };

    const subtotal = useMemo(() => {
        return product_details.reduce(
            (total, item) =>
                total + getCartItemPrice(item) * Number(item.quantity || item.qty || item.cart_quantity || 1),
            0
        );
    }, [product_details]);

    const deliverySlotDates = useMemo(() => {
        const apiSlotDates = normalizeDeliverySlotDates(cartTimeSlotDetails);

        return apiSlotDates;
    }, [cartTimeSlotDetails]);

    const selectedDeliveryDateData = deliverySlotDates.find(
        (dateItem) => dateItem.id === selectedDeliveryDate
    );
    const selectedDeliverySlotData = selectedDeliveryDateData?.slots.find((slot) => slot.value === selectedDeliverySlot);
    const hasDeliverySlots = deliverySlotDates.length > 0;
    const tipStep = getNumberValue(cartSources, ["tip_step", "tip_increment", "driver_tip_step"], TIP_STEP) || TIP_STEP;

    useEffect(() => {
        if (product_details.length === 0) {
            setTipAmount(0);
            setIsTipEdited(false);
        }
    }, [product_details.length]);

    useEffect(() => {
        if (
            selectedDeliveryDate &&
            !deliverySlotDates.some((dateItem) => dateItem.id === selectedDeliveryDate)
        ) {
            setSelectedDeliveryDate("");
            setSelectedDeliverySlot("");
        }
    }, [deliverySlotDates, selectedDeliveryDate]);

    useEffect(() => {
        if (selectedDeliverySlot && !selectedDeliverySlotData) setSelectedDeliverySlot("");
    }, [selectedDeliverySlotData, selectedDeliverySlot]);

    useEffect(() => {
        setOrderError("");
    }, [selectedBagType, deliveryMode, selectedDeliveryDate, selectedDeliverySlot, selectedPaymentOption]);

    const handleInstantDelivery = () => {
        setDeliveryMode("instant");
        setSelectedDeliveryDate("");
        setSelectedDeliverySlot("");
    };

    const handleDeliveryDateClick = (dateId) => {
        setDeliveryMode("scheduled");
        setSelectedDeliveryDate(dateId);
        setSelectedDeliverySlot("");
    };

    const increaseTipAmount = () => {
        if (product_details.length === 0) return;
        setIsTipEdited(true);
        setTipAmount((currentTip) => (isTipEdited ? currentTip : apiTipAmount) + tipStep);
    };

    const decreaseTipAmount = () => {
        setIsTipEdited(true);
        setTipAmount((currentTip) => Math.max(0, (isTipEdited ? currentTip : apiTipAmount) - tipStep));
    };

    const receiptCurrency = getFirstValue([receiptData], ["currency", "currency_code"]);
    const currency =
        receiptCurrency ||
        getCartItemCurrency(product_details[0], "") ||
        getFirstValue([cartData], ["currency", "currency_code"]) ||
        activeMarket.currency;
    const itemTotal = getNumberValue(
        [receiptData],
        ["total_amount"],
        getNumberValue(cartSources, ["cart_amount", "item_total", "sub_total", "subtotal"], subtotal)
    );
    const serviceChargePercent = getNumberValue(
        cartSources,
        ["tax_in_percent", "service_charge_percent", "service_charges_percentage", "service_charge_percentage"],
        0
    );
    const serviceCharge = getNumberValue(
        cartSources,
        ["tax_amount", "time_charges", "service_charge", "service_charges", "service_charge_amount"],
        itemTotal * (serviceChargePercent / 100)
    );
    const deliveryCharge = getNumberValue(
        cartSources,
        ["delivery_amount", "delivery_charge", "delivery_charges", "delivery_fee"],
        0
    );
    const couponAmount = getNumberValue(
        cartSources,
        ["discount_amount", "coupon_amount", "coupon_discount", "discount"],
        0
    );
    const displayCouponAmount = appliedCouponAmount !== null ? appliedCouponAmount : couponAmount;
    const apiTipAmount = getNumberValue(cartSources, ["tip_amount", "tip", "driver_tip"], 0);
    const displayTipAmount = isTipEdited ? tipAmount : apiTipAmount;
    const apiFinalAmountValue = getFirstValue(
        cartSources,
        ["final_amount", "grand_total", "payable_amount", "amount"]
    );
    const apiGrandTotalValue = hasValue(apiFinalAmountValue)
        ? apiFinalAmountValue
        : getFirstValue([cartData], ["total_amount"]);
    const apiGrandTotal = toNumber(apiGrandTotalValue);
    const calculatedGrandTotal = itemTotal + serviceCharge + deliveryCharge - couponAmount + displayTipAmount;
    const grandTotal =
        hasValue(apiGrandTotalValue) && displayTipAmount === apiTipAmount
            ? apiGrandTotal
            : calculatedGrandTotal;
    const totalItems = product_details.reduce(
        (total, item) => total + Number(item.quantity || item.qty || item.cart_quantity || 1),
        0
    );
    const formatMoney = (amount) => `${currency} ${toNumber(amount, 0).toFixed(2)}`;
    const cartId =
        cartState.cartId ||
        getFirstValue([cartData, receiptData], ["cart_id", "cartId", "id"]);
    useEffect(() => {
        setPendingRemoval(null);
        setRemoveError("");
    }, [cartId]);
    const promoSegmentId = getFirstValue(
        [cartData, receiptData, product_details[0]],
        ["segment_id", "segmentId", "segment"]
    );
    const showPrescriptionUpload = Number(promoSegmentId) === 11;
    useEffect(() => {
        setPrescriptionImage(null);
    }, [cartId, promoSegmentId]);
    const promoBusinessSegmentId = getFirstValue(
        [cartData, receiptData, product_details[0]],
        ["business_segment_id", "businessSegmentId", "store_id", "storeId", "branch_id"]
    );
    const businessSegment = parseJsonValue(cartData.business_segment) || {};
    const mandatoryBagValue = businessSegment.is_shopping_bag_mandatory;
    const isShoppingBagRequired = hasValue(mandatoryBagValue) &&
        Number.isFinite(Number(mandatoryBagValue)) && Number(mandatoryBagValue) !== 0;
    const cartSegment = getSegmentItems(servicesData).find((segment) => String(getSegmentId(segment)) === String(promoSegmentId));
    const orderSegmentSources = [cartData, parseJsonValue(cartData.segment), businessSegment, cartSegment];
    const requiresOrderDetails = orderSegmentSources.some(requiresSegmentAddress) ||
        orderSegmentSources.some((segment) => segment?.slag === "FLOWER_DELIVERY");
    useEffect(() => {
        setSelectedBagType("");
        setDeliveryMode("");
        setSelectedDeliveryDate("");
        setSelectedDeliverySlot("");
        setSelectedPaymentOption("");
        setShowOrderDetails(false);
        setOrderError("");
    }, [cartId]);
    const selectedAddressFromAuth = authState.address || authState.user_location?.address;
    const deliveryAddress =
        formatAddress(
            selectedAddressFromAuth ||
            getFirstValue(
                cartSources,
                [
                    "delivery_address",
                    "user_delivery_address",
                    "selected_address",
                    "address_details",
                    "user_address",
                    "address",
                ]
            )
        ) || "Delivery address not available";
    const minimumOrderAmount = getNumberValue(
        cartSources,
        ["minimum_order_amount", "min_order_amount", "minimum_cart_amount"],
        10
    );
    const freeDeliveryAmount = getNumberValue(
        cartSources,
        ["free_delivery_eligibility", "free_delivery_amount", "free_delivery_minimum_amount"],
        90
    );
    const instantDeliveryValue = getFirstValue(
        cartSources,
        ["instant_delivery_text", "instant_delivery", "delivery_time_text", "delivery_time"]
    );
    const instantDeliveryLabel =
        (typeof instantDeliveryValue === "object"
            ? getValue(instantDeliveryValue, ["label", "title", "name", "time"])
            : instantDeliveryValue) || "Instant Delivery (30 min)";
    const storeTitle =
        getFirstValue(
            cartSources,
            ["store_name", "business_name", "segment_name", "store_title", "title"]
        ) || "Daily Deal";
    const walletBalance = getNumberValue(
        cartSources,
        ["wallet_balance", "wallet_amount"],
        0
    );
    const loyaltyBalance = getNumberValue(
        cartSources,
        ["loyalty_points", "loyalty_balance"],
        0
    );
    const apiPaymentOptions = useMemo(() => normalizePaymentMethods(
        payment_method ||
        cartData.payment_methods ||
        cartData.payment_options ||
        cartData.payments ||
        cartData.payment_option_list
    ), [payment_method, cartData.payment_methods, cartData.payment_options, cartData.payments, cartData.payment_option_list]);
    const paymentOptions = useMemo(() => apiPaymentOptions.length
        ? apiPaymentOptions
        : [
            { id: "cash", label: "Cash" },
            { id: "wallet", label: `Wallet (${currency} ${toNumber(walletBalance, 0).toFixed(2)})` },
            { id: "online", label: "Online Payment" },
            { id: "card", label: "Swipe Card" },
        ], [apiPaymentOptions, currency, walletBalance]);
    const selectedPaymentData = paymentOptions.find(
        (option) => String(option.id) === String(selectedPaymentOption)
    ) || {};
    const isCashPaymentSelected = isCashPaymentOption(selectedPaymentData);
    const changeForCashConfig = getFirstValue(cartSources, [
        "change_for_cash_config",
        "changeForCashConfig",
        "cash_change_config",
        "change_for_cash",
    ]);
    const cashChangeOptions = normalizeCashChangeOptions(changeForCashConfig);
    const minimumOrderProgress = getProgressPercent(itemTotal, minimumOrderAmount);
    const freeDeliveryProgress = getProgressPercent(itemTotal, freeDeliveryAmount);
    const apiAllowSubstitution = getBooleanValue(
        cartSources,
        ["allow_substitution", "substitution_allowed", "is_substitution", "is_substitution_allowed"],
        false
    );
    const showSubstitution = getBooleanValue(
        cartSources,
        ["show_substitution", "substitution_enable", "is_substitution_enable"],
        true
    );
    const substitutionTitle =
        getFirstValue(cartSources, ["substitution_title", "allow_substitution_title"]) ||
        "Allow Substitution (Stockout)";
    const substitutionDescription =
        getFirstValue(cartSources, ["substitution_description", "substitution_text", "substitution_message"]) ||
        "If any item gets out of stock, we will give available substitute items against the same.";
    const tipTitle =
        getFirstValue(cartSources, ["tip_title", "driver_tip_title"]) ||
        "Do you want to leave a tip?";
    const tipDescription =
        getFirstValue(cartSources, ["tip_description", "driver_tip_description", "tip_message"]) ||
        "100% of the tip goes to the driver";
    const loyaltyLabel =
        getFirstValue(cartSources, ["loyalty_title", "loyalty_label"]) ||
        "Loyalty Points";
    const shoppingBags = getFirstValue(cartSources, ["shopping_bags", "bag_options", "bag_types", "bags"]);
    const bagOptions = useMemo(() => normalizeShoppingBagOptions(
        shoppingBags,
        currency
    ), [shoppingBags, currency]);
    const selectedBagOption = bagOptions.find((option) => String(option.id) === String(selectedBagType));
    const apiCouponOffers = normalizeSelectableOptions(promoCodeData, []);
    const cartCouponOffers = normalizeSelectableOptions(
        getFirstValue(cartSources, ["coupon_list", "coupons", "promo_codes", "promo_code_list"]),
        []
    );
    const couponOffers = apiCouponOffers.length ? apiCouponOffers : cartCouponOffers;
    const apiBagTypeValue =
        getFirstValue(cartSources, ["selected_bag_type", "shopping_bag_id", "bag_type", "shopping_bag"]) || "";
    const apiBagType =
        typeof apiBagTypeValue === "object"
            ? getValue(apiBagTypeValue, ["id", "bag_id", "shopping_bag_id", "label", "name", "title", "bag_name"])
            : apiBagTypeValue;
    const apiCouponValue =
        getFirstValue(cartSources, ["applied_coupon", "coupon_code", "promo_code", "coupon"]) || "";
    const apiCouponCode =
        typeof apiCouponValue === "object"
            ? getValue(apiCouponValue, ["code", "coupon_code", "promo_code", "label", "name"])
            : apiCouponValue;
    const apiSpecialNotes =
        getFirstValue(cartSources, ["special_notes", "notes", "instruction", "instructions"]) || "";
    const apiSelectedPaymentValue =
        getFirstValue(cartSources, ["selected_payment_method", "selected_payment_option", "payment_mode"]) ||
        "";
    const apiSelectedPaymentOption =
        typeof apiSelectedPaymentValue === "object"
            ? getValue(apiSelectedPaymentValue, ["id", "code", "label", "name", "payment_method"])
            : apiSelectedPaymentValue;
    const reviewOrderText =
        getFirstValue(cartSources, ["review_order_text", "order_review_note"]) ||
        "Review your order and address details to avoid Cancellations";
    const cancellationText =
        getFirstValue(cartSources, ["cancellation_text", "cancellation_policy", "cancel_order_note"]) ||
        `If you choose to cancel, you can do it within 10 minutes. A ${currency} 5.00 cancellation fee may apply.`;
    const cartTitle = getFirstValue(cartSources, ["cart_title", "basket_title"]) || "Cart";
    const emptyCartText = getFirstValue(cartSources, ["empty_cart_text", "cart_empty_text"]) || "Cart empty";
    const updatingCartText = getFirstValue(cartSources, ["updating_cart_text", "cart_loading_text"]) || "Updating cart...";
    const changeAddressLabel = getFirstValue(cartSources, ["change_address_label", "change_address_text"]) || "Change";
    const deliveryAtTitle = getFirstValue(cartSources, ["delivery_at_title", "delivery_address_title"]) || "Delivery At";
    const minimumOrderLabel = getFirstValue(cartSources, ["minimum_order_label", "minimum_order_title"]) || "Minimum order amount";
    const freeDeliveryLabel = getFirstValue(cartSources, ["free_delivery_label", "free_delivery_title"]) || "Free delivery Eligibility";
    const deliverySlotTitle = getFirstValue(cartSources, ["delivery_slot_title", "slot_title"]) || "Choose Delivery Slot";
    const noDeliverySlotsText = getFirstValue(cartSources, ["no_delivery_slots_text", "no_slots_text"]) || "No delivery slots available";
    const paymentSectionTitle = getFirstValue(cartSources, ["payment_title", "payment_section_title"]) || "Payment Options";
    const cashChangeTitle = getFirstValue(cartSources, ["cash_change_title", "change_for_cash_title"]) || "Change for cash";
    const notesTitle = getFirstValue(cartSources, ["notes_title", "special_notes_title"]) || "Special Notes";
    const notesPlaceholder =
        getFirstValue(cartSources, ["notes_placeholder", "special_notes_placeholder"]) ||
        "Additional any instruction (Optional)";
    const placeOrderButtonLabel = getFirstValue(cartSources, ["place_order_label", "place_order_text"]) || "Place Order";
    const bagButtonLabel = getFirstValue(cartSources, ["bag_button_label", "bag_type_label"]) || "Select Bag Type";
    const promoButtonLabel = getFirstValue(cartSources, ["promo_button_label", "coupon_button_label"]) || "Apply Promo Code";
    const balanceLabel = getFirstValue(cartSources, ["balance_label", "loyalty_balance_label"]) || "Balance";
    const itemTotalLabel = getFirstValue(cartSources, ["item_total_label", "cart_amount_label"]) || "Item Total";
    const serviceChargeName = getFirstValue(cartSources, ["tax_name"]);
    const fallbackServiceChargeName = getFirstValue(cartSources, ["service_charges_label", "service_charge_label"]) || "Service Charges";
    const serviceChargesLabel = serviceChargeName || `${fallbackServiceChargeName}${
        serviceChargePercent > 0 ? ` (${serviceChargePercent.toFixed(2)} %)` : ""
    }`;
    const deliveryChargesLabel = getFirstValue(cartSources, ["delivery_charges_label", "delivery_charge_label"]) || "Delivery Charges";
    const couponLabel = getFirstValue(cartSources, ["coupon_label", "discount_label"]) || "Coupon";
    const tipAmountLabel = getFirstValue(cartSources, ["tip_amount_label", "tip_label"]) || "Tip Amount";
    const grandTotalLabel = getFirstValue(cartSources, ["grand_total_label", "total_label"]) || "Grand Total";
    const itemsLabel = getFirstValue(cartSources, ["items_label", "cart_items_label"]) || (totalItems === 1 ? "item" : "items");

    useEffect(() => {
        if (product_details.length > 0 && !isTipEdited) {
            setTipAmount(apiTipAmount);
        }
    }, [apiTipAmount, isTipEdited, product_details.length]);

    useEffect(() => {
        if (!showCouponModal) return;

        let isActive = true;
        const promoCodePayload = new FormData();
        appendIfValue(promoCodePayload, "cart_id", cartId);
        appendIfValue(promoCodePayload, "segment_id", promoSegmentId);
        appendIfValue(promoCodePayload, "business_segment_id", promoBusinessSegmentId);
        appendIfValue(promoCodePayload, "cart_amount", itemTotal);
        appendIfValue(promoCodePayload, "final_amount", grandTotal);

        setPromoCodeLoading(true);

        dispatch(getPromoCodeList(promoCodePayload))
            .then((promoResponse) => {
                if (!isActive) return;
                setPromoCodeData(promoResponse?.data || promoResponse || []);
            })
            .catch(() => {
                if (!isActive) return;
                setPromoCodeData([]);
            })
            .finally(() => {
                if (isActive) {
                    setPromoCodeLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [
        cartId,
        dispatch,
        grandTotal,
        itemTotal,
        promoBusinessSegmentId,
        promoSegmentId,
        showCouponModal,
    ]);

    useEffect(() => {
        setAllowSubstitution(apiAllowSubstitution);
    }, [apiAllowSubstitution]);

    useEffect(() => {
        if (!isShoppingBagRequired) {
            setSelectedBagType("");
            setShowBagTypeModal(false);
            return;
        }
        const apiOption = bagOptions.find((option) => [option.id, option.shoppingBagId, option.label, option.name, option.bag_name].some((value) => hasValue(value) && String(value) === String(apiBagType)));
        setSelectedBagType((current) => bagOptions.some((option) => String(option.id) === String(current)) ? current : apiOption?.id || "");
    }, [apiBagType, bagOptions, cartId, isShoppingBagRequired]);

    useEffect(() => {
        if (apiCouponCode) {
            setAppliedCoupon((currentCoupon) => currentCoupon || apiCouponCode);
        }
    }, [apiCouponCode]);

    useEffect(() => {
        if (apiSpecialNotes) {
            setSpecialNotes((currentNotes) => currentNotes || apiSpecialNotes);
        }
    }, [apiSpecialNotes]);

    useEffect(() => {
        if (!paymentOptions.length) return;

        const defaultPaymentOption =
            paymentOptions.find(
                (option) =>
                    String(option.id) === String(apiSelectedPaymentOption) ||
                    String(option.label) === String(apiSelectedPaymentOption) ||
                    String(option.code) === String(apiSelectedPaymentOption)
            );

        setSelectedPaymentOption((currentPaymentOption) => {
            const isCurrentOptionAvailable = paymentOptions.some(
                (option) => String(option.id) === String(currentPaymentOption)
            );

            return isCurrentOptionAvailable ? currentPaymentOption : defaultPaymentOption?.id ?? "";
        });
    }, [apiSelectedPaymentOption, paymentOptions, cartId]);

    useEffect(() => {
        if (!isCashPaymentSelected || cashChangeOptions.length === 0) {
            setSelectedCashChangeOption("");
            return;
        }

        setSelectedCashChangeOption((currentOption) => {
            const isCurrentOptionAvailable = cashChangeOptions.some(
                (option) => String(option.value) === String(currentOption)
            );

            return isCurrentOptionAvailable ? currentOption : cashChangeOptions[0].value;
        });
    }, [cashChangeOptions, isCashPaymentSelected]);

    return (
        <section className="cart">
            <div className="container container-lg">
                <h5>{cartTitle}</h5>
                <div className="row gy-4">
                    <div className="cart-products-col col-xl-9 col-lg-8">
                        <div className="cart-table cart-product-panel border border-gray-100 rounded-8">
                            <div className="cart-product-header" aria-hidden="true">
                                <span>Product Name</span>
                                <span>Price</span>
                                <span>Quantity</span>
                                <span>Subtotal</span>
                                <span>Delete</span>
                            </div>
                            <div
                                className={`cart-product-list ${isCartLoading && product_details?.length > 0 ? "cart-product-list--updating" : ""}`}
                                aria-label={cartTitle}
                                aria-busy={isCartLoading}
                            >
                                {isCartLoading && product_details?.length > 0 && (
                                    <div className="cart-product-sync-chip">
                                        <span className="cart-product-sync-dot" />
                                        {updatingCartText}
                                    </div>
                                )}

                                {isCartLoading && product_details?.length === 0 && (
                                    Array.from({ length: 3 }).map((_, index) => (
                                        <div className="cart-product-item cart-product-item--skeleton" key={`cart-loader-${index}`}>
                                            <span className="cart-product-skeleton-thumb" />
                                            <span className="cart-product-skeleton-content">
                                                <span className="cart-product-skeleton-line cart-product-skeleton-line--wide" />
                                                <span className="cart-product-skeleton-line cart-product-skeleton-line--short" />
                                                <span className="cart-product-skeleton-line cart-product-skeleton-line--price" />
                                            </span>
                                            <span className="cart-product-skeleton-actions">
                                                <span />
                                                <span />
                                            </span>
                                        </div>
                                    ))
                                )}

                                {!isCartLoading && product_details?.length === 0 && (
                                    <div className="cart-product-state">{emptyCartText}</div>
                                )}

                                {product_details?.length > 0 && product_details?.map((item) => {
                                    const cartItemId = getCartItemId(item);
                                    const itemName = getCartItemName(item);
                                    const itemPrice = getCartItemPrice(item);
                                    const itemCurrency = receiptCurrency || getCartItemCurrency(item, currency);
                                    const detailLink = getProductDetailLink({ ...item, currency: itemCurrency }, { cartContext: { segment_id: item.segment_id || cartData.segment_id, business_segment_id: item.business_segment_id || cartData.business_segment_id } });
                                    const itemImage = getCartItemImage(item);
                                    const itemUnit = getCartItemUnit(item);
                                    const itemQuantity = Number(item.quantity || item.qty || item.cart_quantity || 1);

                                    return (
                                        <article
                                            className={`cart-product-item ${isCartLoading ? "cart-product-item--updating" : ""}`}
                                            key={cartItemId}
                                        >
                                            <Link
                                                {...detailLink}
                                                className="cart-product-thumb"
                                                aria-label={itemName}
                                            >
                                                <ProductImage src={itemImage} alt={itemName} />
                                            </Link>

                                            <div className="cart-product-content">
                                                <Link
                                                    {...detailLink}
                                                    className="cart-product-name"
                                                    tabIndex={0}
                                                >
                                                    {itemName}
                                                </Link>
                                                {itemUnit && (
                                                    <span className="cart-product-unit">{itemUnit}</span>
                                                )}
                                            </div>

                                            <span className="cart-product-price-cell">
                                                {itemCurrency} {Number(itemPrice || 0).toFixed(2)}
                                            </span>

                                            <div className="cart-product-quantity-cell">
                                                <div className="cart-product-quantity">
                                                    <QuantityControl
                                                        initialQuantity={itemQuantity}
                                                        disabled={isCartLoading}
                                                        onChange={(qty) => updateQuantity(cartItemId, qty)}
                                                    />
                                                </div>
                                            </div>

                                            <span className="cart-product-subtotal">
                                                {itemCurrency} {(Number(itemPrice || 0) * itemQuantity).toFixed(2)}
                                            </span>

                                            <div className="cart-product-actions">
                                                <button
                                                    type="button"
                                                    className="cart-product-remove"
                                                    onClick={() => removeProduct(cartItemId)}
                                                    disabled={isCartLoading}
                                                    aria-label={`Remove ${itemName}`}
                                                >
                                                    <i className="ph-fill ph-trash" />
                                                </button>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                            {/*<div className="flex-between flex-wrap gap-16 mt-16">
                                <div className="flex-align gap-16">
                                    <input
                                        type="text"
                                        className="common-input"
                                        placeholder="Coupon Code"
                                    />
                                    <button
                                        type="submit"
                                        className="btn btn-main py-18 w-100 rounded-8"
                                    >
                                        Apply Coupon
                                    </button>
                                </div>
                                <button
                                    type="submit"
                                    className="text-lg text-gray-500 hover-text-main-600"
                                >
                                    Update Cart
                                </button>
                            </div>*/}
                        </div>
                    </div>
                    <div className="cart-sidebar-col col-xl-3 col-lg-4">
                      <div className="cart-sidebar border border-gray-100 rounded-12 px-5 py-24 custom-sidebar">

                        {/* Delivery At */}
                        <h6 className="sidebar-title">{deliveryAtTitle}</h6>

                        <div className="delivery-box">
                          <p>
                           {deliveryAddress}
                          </p>
                          <button
                            type="button"
                            className="bg-color-primary text-white change-btn"
                            onClick={() => navigate('/address-list')}
                          >
                            {changeAddressLabel}
                          </button>
                        </div>

                        {/* Order Info */}
                        <div className="info-box">
                          <p>{minimumOrderLabel}: {formatMoney(minimumOrderAmount)}</p>
                          <div className="progress-bar-custom">
                            <div
                              className="progress-fill"
                              style={{ width: `${minimumOrderProgress}%` }}
                            ></div>
                          </div>
                          <p>{freeDeliveryLabel}: {formatMoney(freeDeliveryAmount)}</p>
                          <div className="progress-bar-custom">
                            <div
                              className="progress-fill"
                              style={{ width: `${freeDeliveryProgress}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Substitution */}
                        {showSubstitution && (
                          <div className="substitution-box">
                            <div>
                              <strong>{substitutionTitle}</strong>
                              <p>{substitutionDescription}</p>
                            </div>
                            <label className="switch">
                              <input
                                type="checkbox"
                                checked={allowSubstitution}
                                onChange={(event) => setAllowSubstitution(event.target.checked)}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        )}

                        {/* Tip Section */}
                        <div className="tip-section">
                          <p className="fw-semibold">{tipTitle}</p>
                          <small>{tipDescription}</small>

                          <div className="tip-box">
                            <button type="button" className="tip-currency active">{currency}</button>

                            <div className="tip-counter">
                              <button
                                type="button"
                                onClick={decreaseTipAmount}
                                disabled={displayTipAmount === 0}
                                aria-label="Decrease tip amount"
                              >
                                -
                              </button>
                                                            <input
                                                                type="text"
                                                                className="tip-amount-input"
                                                                value={displayTipAmount}
                                                                readOnly
                                                                aria-label="Tip amount"
                                                            />
                              <button
                                type="button"
                                onClick={increaseTipAmount}
                                disabled={product_details.length === 0}
                                aria-label="Increase tip amount"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Loyalty: hidden until points redemption is available. */}
                        <div className="loyalty-box d-none">
                          <div>
                            <input type="checkbox" /> {loyaltyLabel}
                          </div>
                          <span>{balanceLabel}: {loyaltyBalance}</span>
                        </div>

                        {/* Bag Type */}
                        {isShoppingBagRequired && <button
                          type="button"
                          className="bag-btn"
                          onClick={() => setShowBagTypeModal(true)}
                        >
                          {selectedBagOption?.label || bagButtonLabel}{isShoppingBagRequired ? " *" : ""} <span>›</span>
                        </button>}

                        {/* Apply Promo */}
                        <button
                          type="button"
                          className="promo-btn"
                          onClick={() => setShowCouponModal(true)}
                        >
                          {appliedCoupon || promoButtonLabel}
                        </button>

                        {couponMessage.text && (
                          <div
                            style={{
                              marginTop: "12px",
                              color: couponMessage.type === "error" ? "#d9534f" : "#28a745",
                              fontSize: "0.95rem",
                            }}
                          >
                            {couponMessage.text}
                          </div>
                        )}

                        {/* Price Breakdown */}
                        <div className="price-breakdown">
	                          <div className="row-item"><span>{itemTotalLabel}</span><span>{formatMoney(itemTotal)}</span></div>
	                          <div className="row-item"><span>{serviceChargesLabel}</span><span>{formatMoney(serviceCharge)}</span></div>
	                          <div className="row-item"><span>{deliveryChargesLabel}</span><span>{formatMoney(deliveryCharge)}</span></div>
	                          <div className="row-item"><span>{couponLabel}</span><span>{displayCouponAmount !== 0 ? `- ${formatMoney(Math.abs(displayCouponAmount))}` : formatMoney(0)}</span></div>
	                          <div className="row-item"><span>{tipAmountLabel}</span><span>{formatMoney(displayTipAmount)}</span></div>

	                          <div className="row-item total-row">
	                            <strong>{grandTotalLabel}</strong>
	                            <strong>{formatMoney(grandTotal)}</strong>
	                          </div>
                        </div>

                        {showPrescriptionUpload && <PrescriptionUpload
                          file={prescriptionImage}
                          onChange={setPrescriptionImage}
                          disabled={isCartLoading || submittingOrder}
                        />}

                        {/* Notes */}
                        <div className="notes-box">
                          <span className="notes-box__title">{notesTitle}</span>
                          <textarea
                            placeholder={notesPlaceholder}
                            value={specialNotes}
                            onChange={(event) => setSpecialNotes(event.target.value)}
                          ></textarea>
                        </div>

                      </div>
                    </div>
                    <div className="col-xl-12 col-lg-12">
                        <div className="text-box">
                            <p>{reviewOrderText}</p>
                            <p>{cancellationText}</p>
                        </div>  
                    </div>     
                    <div className="col-xl-12 col-lg-12">    
                        <div className="delivery-section-bg">
                            <p>{deliverySlotTitle}</p>
                            <label className="custom-radio py-10">
                              <input
                                type="radio"
                                name="delivery"
                                checked={deliveryMode === "instant"}
                                onChange={handleInstantDelivery}
                              />
                              <span className="radio-mark"></span>
                              <span className="text-span">{instantDeliveryLabel}</span>
                            </label>
                            <div className="delivery-date-row">
                              <label className="custom-radio">
                                <input
                                  type="radio"
                                  name="delivery"
                                  aria-label="Scheduled delivery"
                                  checked={deliveryMode === "scheduled"}
                                  disabled={!hasDeliverySlots}
                                  onChange={() => {
                                    if (!hasDeliverySlots) return;
                                    setDeliveryMode("scheduled");
                                    setSelectedDeliveryDate(
                                      (currentDate) => currentDate || deliverySlotDates[0]?.id || ""
                                    );
                                  }}
                                />
                                <span className="radio-mark"></span>
                              </label>

                              <div className="delivery-date-buttons">
                                {hasDeliverySlots ? deliverySlotDates.map((dateItem) => (
                                  <button
                                    type="button"
                                    key={dateItem.id}
                                    className={`text-button ${
                                      selectedDeliveryDate === dateItem.id ? "active" : ""
                                    }`}
                                    onClick={() => handleDeliveryDateClick(dateItem.id)}
                                  >
                                    {dateItem.label}
                                  </button>
                                )) : (
                                  <span className="text-span">{noDeliverySlotsText}</span>
                                )}
                              </div>
                            </div>

                            {deliveryMode === "scheduled" && selectedDeliveryDateData && (
                              <div className="delivery-slot-list">
                                {selectedDeliveryDateData.slots.map((slot) => (
                                  <label
                                    className="custom-radio delivery-slot-option"
                                    key={`${selectedDeliveryDateData.id}-${slot.value}`}
                                  >
                                    <input
                                      type="radio"
                                      name="slot"
                                      value={slot.value}
                                      checked={selectedDeliverySlot === slot.value}
                                      onChange={() => setSelectedDeliverySlot(slot.value)}
                                    />
                                    <span className="radio-mark"></span>
                                    <span className="text-span">{slot.label}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                        </div> 
                        <div className="delivery-section-bg" style={{marginTop: 10}}>
                            <div className="row">
                                <div className="col-xl-12">
                                    <p>{paymentSectionTitle}</p>
                                </div>
                                {paymentOptions.map((option) => {
                                  const isSelectedPayment = String(selectedPaymentOption) === String(option.id);

                                  return (
                                  <div className="col-lg-3 col-md-6" key={option.id || option.label}>
                                    <div
                                      className={`payment-option-section h-100 ${isSelectedPayment ? "is-selected" : ""}`}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setSelectedPaymentOption(option.id)}
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                          event.preventDefault();
                                          setSelectedPaymentOption(option.id);
                                        }
                                      }}
                                    >
                                      <div className="payment-row">
                                        <label className="payment-label">
                                          <img
                                            src={option.icon || `${process.env.PUBLIC_URL}/assets/icon/Group 6878.png`}
                                            alt=""
                                          />
                                          <span className="text-span">{option.label}</span>
                                        </label>

                                        <label className="custom-radio">
                                          <input
                                            type="radio"
                                            name="paymentOption"
                                            aria-label={option.label}
                                            checked={isSelectedPayment}
                                            onChange={() => setSelectedPaymentOption(option.id)}
                                          />
                                          <span className="radio-mark"></span>
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                  );
                                })}
                                
                                {isCashPaymentSelected && cashChangeOptions.length > 0 && (
                                  <div className="col-12">
                                    {cashChangeOptions.map((option) => (
                                      <div className="col-lg-3 col-md-6" key={option.id} style={{ marginBottom: "12px" }}>
                                        <label
                                          className="custom-radio"
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            padding: "10px 35px",
                                            borderRadius: "4px",
                                            border: "1px solid #f0f0f0",
                                          }}
                                        >
                                          <span className="text-span" style={{ fontSize: "13px" }}>
                                            {option.label}
                                          </span>
                                          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <input
                                              type="radio"
                                              name="cashChange"
                                              value={option.value}
                                              checked={String(selectedCashChangeOption) === String(option.value)}
                                              onChange={() => setSelectedCashChangeOption(option.value)}
                                            />
                                            <span className="radio-mark"></span>
                                          </span>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                        </div> 
                    </div>   
                    <div className="col-xl-12 col-lg-12">
                        <div className="bg-color-primary place-button-section">
                        {orderError && !showOrderDetails && <p ref={orderErrorRef} tabIndex={-1} className="cart-checkout-error" role="alert">{orderError}</p>}
                        {/* Checkout summary aligned with the header container. */}
                        <div className="container container-lg payment-row">
                          <div className="payment-label">
                            <img
                              src={`${process.env.PUBLIC_URL}/assets/icon/basket.png`}
                              alt=""
                              aria-hidden="true"
                            />
                            <div>
                                <p className="text-span cart-checkout-total">{formatMoney(grandTotal)} <span className="cart-checkout-item-count">({totalItems} {String(itemsLabel).toLowerCase()})</span></p>
                                <p className="text-span">{storeTitle}</p>
                            </div>
                          </div>

                          <div className="custom-place-button">
                            <button
                              type="button"
                              onClick={handlePlaceOrder}
                              disabled={product_details.length === 0 || isCartLoading || submittingOrder}
                            >
                              {submittingOrder ? "Placing order..." : placeOrderButtonLabel}
                            </button>
                          </div>
                        </div>
                        </div>  
                    </div>  
                </div>
            </div>
            <ThankYouPopup 
                show={showPopup} 
                onClose={() => setShowPopup(false)} 
            />
            <OrderDetailsModal
                show={showOrderDetails}
                address={deliveryAddress}
                senderName={authState.userData?.name || authState.userData?.user_name || ""}
                countryCode={getMarketMetadata(authState.user_location || userLocation).countryCode}
                initialAddress={authState.user_location}
                submitting={submittingOrder}
                error={orderError}
                onClose={() => { if (!orderRequestRef.current) { setShowOrderDetails(false); setOrderError(""); } }}
                onSubmit={submitOrder}
            />
            <SelectBagTypyModel
                show={isShoppingBagRequired && showBagTypeModal}
                onClose={() => setShowBagTypeModal(false)}
                options={bagOptions}
                selectedValue={selectedBagType}
                onSubmit={(_, option) => {
                    setSelectedBagType(typeof option === "string" ? option : option.id);
                    setShowBagTypeModal(false);
                }}
            />
            <ApplyCouponModal
                show={showCouponModal}
                onClose={() => setShowCouponModal(false)}
                offers={couponOffers}
                loading={promoCodeLoading}
                onApply={handleApplyCoupon}
                message={couponMessage}
            />
            <RemoveCartItemModal
                show={Boolean(pendingRemoval)}
                productName={pendingRemoval?.name || "this item"}
                busy={removingProduct}
                error={removeError}
                onClose={closeRemoveConfirmation}
                onConfirm={confirmRemoveProduct}
            />
        </section>

    )
}

export default CartSection
